// Multi-marc round state. Owns the lineup + currentIndex + complete
// flag; nothing else. Per-marc play state (target picker, detection,
// celebration) lives in MarcEngine. Lives outside React; the binding
// hook (useSpeedrunEngine) handles the React-side glue.
//
// Inputs:
//   start({ lineup, available }) — begin a round with this marc list,
//     and remember the full available list so cycleMarc can walk it.
//     Does NOT stamp startedAt — the timer is deferred until the egg
//     is actually in position (App calls markStarted() on first tween-
//     end).
//   markStarted()                — first time the egg has settled at
//     the round's first marc. Stamps startedAt. Idempotent.
//   advanceMarc()                — current marc just hit allDone.
//   cycleMarc(delta)             — debug-only: replace the current
//     entry in the lineup with the next/prev marc from `available`.
//     Preserves round shape (lineup length stays the same).
//   reset()                      — drop all state.
//
// Snapshot: { currentMarcId, currentIndex, total, complete }. Listeners
// observe snapshots; downstream side effects (clearing the canvas
// between marcs) ride on the snapshot's currentMarcId via a React
// useEffect rather than an imperative callback from here.

export default class SpeedrunEngine {
  constructor() {
    this.lineup = [];
    this.available = [];
    this.currentIndex = 0;
    this.complete = false;
    // Round timer is count-UP — final score is total elapsed time. Both
    // millisecond timestamps from Date.now(); startedAt is stamped by
    // markStarted() (called by App when the first tween settles), and
    // endedAt is stamped exactly once when the round flips to complete
    // (so the displayed time freezes at the moment the last marc is
    // finished, not whenever the ScorePanel happens to render next).
    this.startedAt = null;
    this.endedAt = null;
    this.listeners = new Set();
  }

  start({ lineup, available }) {
    this.lineup = [...lineup];
    this.available = [...(available ?? lineup)];
    this.currentIndex = 0;
    this.complete = this.lineup.length === 0;
    // startedAt stays null until markStarted(); the round's "is the
    // timer running yet?" signal is the snapshot's startedAt being
    // non-null. ScorePanel's RoundTimer already shows 0:00.0 in that
    // case.
    this.startedAt = null;
    this.endedAt = null;
    this._emit();
  }

  // Idempotent: called by App when the first tween-end transition
  // fires in a fresh round. Subsequent invocations no-op so we don't
  // accidentally re-stamp startedAt mid-round (e.g., on a marc-to-
  // marc tween-end).
  markStarted() {
    if (this.startedAt != null) return;
    if (this.complete) return;
    if (this.lineup.length === 0) return;
    this.startedAt = Date.now();
    this._emit();
  }

  advanceMarc() {
    if (this.complete) return;
    if (this.currentIndex + 1 >= this.lineup.length) {
      this.complete = true;
      this.endedAt = Date.now();
    } else {
      this.currentIndex += 1;
    }
    this._emit();
  }

  // Debug-only: replace the current lineup slot with the next/prev marc
  // from `available`. If the candidate is the same as the current id
  // (lineup of 1 with only one available), no-ops. Lineup is replaced
  // with a new array (immutable update) rather than spliced in place so
  // snapshot consumers can compare identities to detect real changes.
  cycleMarc(delta) {
    if (this.available.length === 0 || this.lineup.length === 0) return;
    const current = this.lineup[this.currentIndex];
    const i = this.available.indexOf(current);
    const len = this.available.length;
    // If current isn't in `available` (e.g. pinned via ?marcs= to an id
    // not in manifest), fall back to index 0 so cycling still works.
    const base = i === -1 ? 0 : i;
    const next = this.available[(base + delta + len) % len];
    if (next === current) return;
    this.lineup = this.lineup.map((id, idx) =>
      idx === this.currentIndex ? next : id,
    );
    this._emit();
  }

  reset() {
    this.lineup = [];
    this.available = [];
    this.currentIndex = 0;
    this.complete = false;
    this.startedAt = null;
    this.endedAt = null;
    this._emit();
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  dispose() {
    this.listeners.clear();
  }

  getSnapshot() {
    // In complete state we still expose the last marc as currentMarcId
    // so the reference photo + canvas don't flicker back to a default
    // while the round-complete UI catches up. Goes null only when the
    // engine is fully reset (lineup empty).
    //
    // `lineup` is exposed by reference. All mutations (start, cycleMarc,
    // reset) replace this.lineup with a new array, so consumers can
    // compare identities to detect real round-shape changes — useful
    // for memoization downstream (the end-screen modal recomputes
    // podium order off this).
    return {
      currentMarcId:
        this.lineup.length > 0 ? this.lineup[this.currentIndex] : null,
      currentIndex: this.currentIndex,
      lineup: this.lineup,
      total: this.lineup.length,
      complete: this.complete,
      startedAt: this.startedAt,
      endedAt: this.endedAt,
    };
  }

  _emit() {
    for (const l of this.listeners) l();
  }
}
